import React from 'react';
import { formatCPF, formatPhone, formatDate } from '../../utils/formatters';
import { CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';

const getStatusBadge = (status) => {
  const badges = {
    active:   { color: 'bg-green-100 text-green-800',  Icon: CheckCircle, label: 'Ativo' },
    inactive: { color: 'bg-red-100 text-red-800',      Icon: XCircle,     label: 'Inativo' },
    pending:  { color: 'bg-yellow-100 text-yellow-800', Icon: Clock,       label: 'Pendente' },
  };
  const { color, Icon, label } = badges[status] || badges.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3 mr-1" />
      {label}
    </span>
  );
};

const MemberAvatar = ({ member }) =>
  member.photoUrl ? (
    <img
      src={member.photoUrl}
      alt={member.name}
      className="h-9 w-9 rounded-full object-cover flex-shrink-0"
    />
  ) : (
    <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <span className="text-sm font-medium text-gray-600">
        {member.name?.charAt(0).toUpperCase()}
      </span>
    </div>
  );

const actionClass = (tone) =>
  `inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
    tone === 'green'
      ? 'bg-green-100 text-green-800 hover:bg-green-200'
      : 'bg-red-100 text-red-800 hover:bg-red-200'
  }`;

const MemberActions = ({ member, onUpdateStatus, fullWidth = false }) => {
  const extra = fullWidth ? ' flex-1 py-2 text-sm' : '';

  if (member.status === 'pending') {
    return (
      <>
        <button
          onClick={() => onUpdateStatus(member.id, 'active')}
          className={actionClass('green') + extra}
          title="Aprovar associado"
        >
          <CheckCircle className="h-3.5 w-3.5" />
          Aprovar
        </button>
        <button
          onClick={() => onUpdateStatus(member.id, 'inactive')}
          className={actionClass('red') + extra}
          title="Rejeitar associado"
        >
          <XCircle className="h-3.5 w-3.5" />
          Rejeitar
        </button>
      </>
    );
  }

  if (member.status === 'active') {
    return (
      <button
        onClick={() => onUpdateStatus(member.id, 'inactive')}
        className={actionClass('red') + extra}
        title="Inativar associado"
      >
        <XCircle className="h-3.5 w-3.5" />
        Inativar
      </button>
    );
  }

  if (member.status === 'inactive') {
    return (
      <button
        onClick={() => onUpdateStatus(member.id, 'active')}
        className={actionClass('green') + extra}
        title="Reativar associado"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Reativar
      </button>
    );
  }

  return null;
};

const MembersTable = ({ members, onUpdateStatus }) => {
  if (!members || members.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Nenhum associado encontrado.
      </div>
    );
  }

  return (
    <>
      {/* Celular: cartões. Na tabela, as ações ficavam na 7ª coluna, fora da
          tela, e não havia como aprovar cadastros pelo celular. */}
      <ul className="md:hidden divide-y divide-gray-200" data-testid="members-cards">
        {members.map((member) => (
          <li key={member.id} className="p-4 space-y-3" data-testid={`member-card-${member.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center space-x-3 min-w-0">
                <MemberAvatar member={member} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{member.name}</div>
                  <div className="text-xs text-gray-500 truncate">{member.email}</div>
                </div>
              </div>
              {getStatusBadge(member.status)}
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <dt className="text-gray-500">Telefone</dt>
              <dd className="text-gray-900 text-right">{formatPhone(member.phone) || '—'}</dd>
              <dt className="text-gray-500">Orixá</dt>
              <dd className="text-gray-900 text-right">{member.orixa || '—'}</dd>
              <dt className="text-gray-500">Cadastro</dt>
              <dd className="text-gray-900 text-right">{formatDate(member.createdAt)}</dd>
              {member.cpf && (
                <>
                  <dt className="text-gray-500">CPF</dt>
                  <dd className="text-gray-900 text-right">{formatCPF(member.cpf)}</dd>
                </>
              )}
            </dl>

            <div className="flex gap-2">
              <MemberActions member={member} onUpdateStatus={onUpdateStatus} fullWidth />
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden md:block overflow-x-auto" data-testid="members-table">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orixá</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cadastro</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="sticky right-0 bg-gray-50 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {members.map((member) => (
              <tr key={member.id} className="group hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <MemberAvatar member={member} />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{member.name}</div>
                      {member.cpf && (
                        <div className="text-xs text-gray-500">{formatCPF(member.cpf)}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{member.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatPhone(member.phone) || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {member.orixa || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(member.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(member.status)}
                </td>
                {/* Coluna fixa: com o menu lateral, a tabela é mais larga que a área
                    disponível e as ações ficavam fora da tela mesmo no computador. */}
                <td className="sticky right-0 bg-white group-hover:bg-gray-50 px-6 py-4 whitespace-nowrap text-right text-sm font-medium shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                  <div className="flex items-center justify-end gap-2">
                    <MemberActions member={member} onUpdateStatus={onUpdateStatus} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default MembersTable;
